import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Target, TrendingUp, Trophy, Clock, Dumbbell, Zap, BarChart3, Moon, Apple, Timer, Calendar, LogOut, Edit, Award, FileText, Edit3 } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { DashboardCard } from "@/components/DashboardCard";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useWorkouts } from "@/hooks/useWorkouts";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ExerciseProgressCharts } from "@/components/ExerciseProgressCharts";
import { ShareIcon } from "@/components/icons/ShareIcon";
import { ExerciseList } from "@/components/ExerciseList";

// New imports for enhanced progress tracking
import { useGoals } from "@/hooks/useGoals";
import { useSleepTracking } from "@/hooks/useSleepTracking";
import { useNutritionTracking } from "@/hooks/useNutritionTracking";
import { useComprehensiveNutrition } from "@/contexts/NutritionContext";
import { GoalProgressCard } from "@/components/GoalProgressCard";
import { SleepTrackingCard } from "@/components/SleepTrackingCard";
import { NutritionTrackingCard } from "@/components/NutritionTrackingCard";
import { MilestoneCelebration } from "@/components/MilestoneCelebration";
import { CombinedProgressChart } from "@/components/CombinedProgressChart";
import { ShareWorkoutModal } from "@/components/ShareWorkoutModal";
import { ShareableWorkout } from "@/components/ShareWorkoutCard";
import { Analytics } from "@/lib/analytics";

export const Progress = () => {
  const { user, signOut } = useAuth();
  
  // Track progress viewed
  useEffect(() => {
    Analytics.progressViewed('overview');
  }, []);
  const { profile, updateProfile } = useProfile();
  const { workouts, getWorkoutStats, getStrengthProgress, getRunningProgress } = useWorkouts();
  const navigate = useNavigate();
  const [editingGoals, setEditingGoals] = useState(false);
  const [tempGoals, setTempGoals] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedWorkoutForShare, setSelectedWorkoutForShare] = useState<ShareableWorkout | null>(null);
  
  // Enhanced progress tracking hooks
  const { 
    goals, 
    milestones, 
    loading: goalsLoading, 
    createGoal, 
    createGoalsFromText,
    updateGoalProgress,
    deleteGoal,
    markMilestoneAsShown 
  } = useGoals();
  
  const { 
    sleepLogs, 
    loading: sleepLoading, 
    logSleep, 
    getWeeklyAverage: getSleepWeeklyAverage, 
    getSleepTrend 
  } = useSleepTracking();
  
  const { 
    nutritionLogs, 
    loading: nutritionLoading, 
    logNutrition, 
    getProteinGoal, 
    getTodaysProtein, 
    getWeeklyProteinAverage, 
    getProteinTrend, 
    getProteinStreaks,
    refreshTodaysProtein
  } = useNutritionTracking();
  
  const { 
    nutritionGoals,
    getNutritionTotalsRange 
  } = useComprehensiveNutrition();
  
  // State for daily nutrition data for Combined Progress Chart
  const [dailyNutritionData, setDailyNutritionData] = useState<any[]>([]);
  
  // Fetch daily nutrition data for chart
  const fetchDailyNutritionData = async () => {
    if (!getNutritionTotalsRange) return;
    
    try {
      const data = await getNutritionTotalsRange(30);
      setDailyNutritionData(data || []);
    } catch (error) {
      console.error('Error fetching daily nutrition data:', error);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchDailyNutritionData();
  }, [getNutritionTotalsRange]);
  
  // Real-time subscription for nutrition updates
  useEffect(() => {
    if (!user) return;
    
    const channel = supabase
      .channel('nutrition-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'food_entries'
        },
        () => {
          console.log('Food entry changed, refreshing nutrition...');
          refreshTodaysProtein();
          fetchDailyNutritionData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'nutrition_logs'
        },
        () => {
          console.log('Nutrition log changed, refreshing...');
          refreshTodaysProtein();
          fetchDailyNutritionData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meals'
        },
        () => {
          console.log('Meal changed, refreshing nutrition...');
          fetchDailyNutritionData();
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refreshTodaysProtein]);

  // Milestone celebration state
  const [showMilestone, setShowMilestone] = useState(false);
  const [activeMilestone, setActiveMilestone] = useState<any>(null);
  const [milestoneGoal, setMilestoneGoal] = useState<any>(null);
  
  // Track shown milestones in this session to prevent re-showing
  const shownMilestonesRef = useRef<Set<string>>(new Set());

  // Check for new milestones - only show ONCE per milestone and only for goal completion
  useEffect(() => {
    if (milestones.length === 0 || showMilestone || activeMilestone) return;
    
    // Find the first unshown GOAL REACHED milestone not yet shown this session
    const unshownMilestone = milestones.find(m => 
      m.milestone_type === 'goal_reached' && !m.celebration_shown && !shownMilestonesRef.current.has(m.id)
    );
    
    if (unshownMilestone) {
      const associatedGoal = goals.find(g => g.id === unshownMilestone.goal_id);
      
      if (associatedGoal) {
        shownMilestonesRef.current.add(unshownMilestone.id);
        setActiveMilestone(unshownMilestone);
        setMilestoneGoal(associatedGoal);
        setShowMilestone(true);
      }
    }
  }, [milestones, goals, showMilestone, activeMilestone]);

  const handleCloseMilestone = async () => {
    if (activeMilestone) {
      await markMilestoneAsShown(activeMilestone.id);
    }
    setShowMilestone(false);
    setActiveMilestone(null);
    setMilestoneGoal(null);
  };

  // Check if user has specific goals but no structured goals, then parse and create them
  useEffect(() => {
    const createInitialGoals = async () => {
      if (profile?.specific_goals && goals.length === 0 && !goalsLoading && user) {
        try {
          console.log('Creating goals from specific_goals text:', profile.specific_goals);
          const createdGoals = await createGoalsFromText(profile.specific_goals);
          
          // If no structured goals were parsed, create a generic goal
          if (!createdGoals || createdGoals.length === 0) {
            console.log('No structured goals parsed, creating generic goal');
            await createGoal({
              goal_type: 'performance',
              exercise_name: null,
              target_value: 100,
              target_unit: 'progress',
              current_value: 0,
              starting_value: 0,
              target_date: null,
              is_active: true,
              description: profile.specific_goals
            });
            toast.success('Goal created from your profile!');
          } else {
            toast.success(`${createdGoals.length} goal(s) created from your profile!`);
          }
        } catch (error) {
          console.error('Error creating initial goals:', error);
          toast.error('Failed to create goals from your profile');
        }
      }
    };

    createInitialGoals();
  }, [profile?.specific_goals, goals.length, goalsLoading, user]);

  const handleCreateGoal = async (goalData: any) => {
    try {
      console.log('Creating goal with data:', goalData);
      await createGoal(goalData);
      Analytics.goalCreated(goalData.goal_type, goalData.exercise_name || 'General');
      toast.success('Goal created successfully!');
    } catch (error: any) {
      console.error('Failed to create goal:', error);
      const errorMessage = error?.message || 'Failed to create goal';
      toast.error(errorMessage);
    }
  };

  const handleUpdateProgress = async (goalId: string, newValue: number) => {
    try {
      await updateGoalProgress(goalId, newValue);
      const goal = goals.find(g => g.id === goalId);
      if (goal) {
        Analytics.goalUpdated(goal.goal_type, goal.exercise_name || 'General');
      }
      toast.success('Progress updated!');
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      await deleteGoal(goalId);
      toast.success('Goal removed successfully');
    } catch (error) {
      toast.error('Failed to remove goal');
    }
  };

  const handleLogSleep = async (sleepData: any) => {
    try {
      await logSleep(sleepData);
      Analytics.sleepLogged(sleepData.hours_slept, sleepData.quality_rating || 5);
      toast.success('Sleep logged successfully!');
    } catch (error) {
      toast.error('Failed to log sleep');
    }
  };

  const handleLogNutrition = async (nutritionData: any) => {
    try {
      await logNutrition(nutritionData);
      toast.success('Nutrition logged successfully!');
    } catch (error) {
      toast.error('Failed to log nutrition');
    }
  };

  const handleGoalsUpdate = async () => {
    try {
      await updateProfile({ specific_goals: tempGoals });
      setEditingGoals(false);
      toast.success("Goals updated successfully!");
    } catch (error) {
      toast.error("Failed to update goals");
    }
  };

  const openEditGoals = () => {
    setTempGoals(profile?.specific_goals || "");
    setEditingGoals(true);
  };

  const handleLogout = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error("Failed to sign out");
    } else {
      toast.success("Signed out successfully");
      navigate("/auth");
    }
  };

  // Helper function to convert workout to shareable format
  const prepareWorkoutForShare = async (workout: any): Promise<ShareableWorkout> => {
    // Fetch exercises for this workout (include notes for running route data)
    const { data: exercises } = await supabase
      .from('exercises')
      .select('name, sets, reps, weight_kg, distance_km, duration_seconds, notes')
      .eq('workout_id', workout.id);
    
    const totalSets = exercises?.reduce((sum, ex) => sum + (ex.sets || 0), 0) || 0;
    const totalVolume = exercises?.reduce((sum, ex) => {
      return sum + ((ex.sets || 0) * (ex.reps || 0) * (ex.weight_kg || 0));
    }, 0) || 0;
    
    // Check if it's a running workout
    const runningExercise = exercises?.find(ex => ex.distance_km && ex.distance_km > 0);
    
    // Extract route coordinates from running exercise notes
    let routeCoordinates: any[] = [];
    if (runningExercise?.notes) {
      try {
        const runData = JSON.parse(runningExercise.notes);
        routeCoordinates = (runData?.routePoints || []).map((p: any) => ({ lat: p.lat, lng: p.lng }));
      } catch {}
    }
    
    const result: any = {
      id: workout.id,
      title: workout.title,
      duration_minutes: workout.duration_minutes || 0,
      completed_at: workout.completed_at,
      workout_type: workout.workout_type,
      total_sets: totalSets,
      total_volume: totalVolume,
      calories_burned: workout.calories_burned ?? null,
      exercises: exercises?.map(ex => ({
        name: ex.name,
        sets: ex.sets || 0,
        reps: ex.reps || undefined,
        weight_kg: ex.weight_kg || undefined,
      })),
      distance_km: runningExercise?.distance_km,
      average_pace: runningExercise?.duration_seconds && runningExercise?.distance_km
        ? formatPace(runningExercise.duration_seconds, runningExercise.distance_km)
        : undefined,
    };
    
    if (runningExercise?.duration_seconds != null) {
      result.duration_seconds = runningExercise.duration_seconds;
    }
    if (runningExercise?.distance_km && runningExercise?.duration_seconds) {
      result.pace_seconds_per_km = runningExercise.duration_seconds / runningExercise.distance_km;
    }
    
    if (routeCoordinates.length > 0) {
      result.route_coordinates = routeCoordinates;
    }
    
    return result;
  };

  const formatPace = (totalSeconds: number, distanceKm: number): string => {
    if (!distanceKm || distanceKm === 0) return '';
    const paceSeconds = totalSeconds / distanceKm;
    const minutes = Math.floor(paceSeconds / 60);
    const seconds = Math.floor(paceSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')} /km`;
  };

  const handleShareWorkout = async (workout: any) => {
    try {
      const shareableWorkout = await prepareWorkoutForShare(workout);
      setSelectedWorkoutForShare(shareableWorkout);
      setShowShareModal(true);
    } catch (error) {
      console.error('Error preparing workout for share:', error);
      toast.error('Failed to prepare workout for sharing');
    }
  };
  // Get real workout statistics  
  const stats = getWorkoutStats();
  const runningProgress = getRunningProgress();
  const completedWorkouts = workouts.filter(w => w.completed_at);
  
  // Calculate total workout time
  const totalWorkoutTime = completedWorkouts.reduce((total, workout) => {
    return total + (workout.duration_minutes || 0);
  }, 0);

  // Calculate workout frequency (workouts per week over last 4 weeks)
  const fourWeeksAgo = new Date();
  fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
  
  const recentWorkouts = completedWorkouts.filter(workout => 
    new Date(workout.completed_at!) >= fourWeeksAgo
  );
  
  const workoutsPerWeek = recentWorkouts.length / 4;

  // Prepare workout trend data (last 8 weeks)
  const weeklyData = [];
  for (let i = 7; i >= 0; i--) {
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - (i * 7) - 6);
    const weekEnd = new Date();
    weekEnd.setDate(weekEnd.getDate() - (i * 7));
    
    const weekWorkouts = completedWorkouts.filter(workout => {
      const workoutDate = new Date(workout.completed_at!);
      return workoutDate >= weekStart && workoutDate <= weekEnd;
    });
    
    // Format date range for display
    const dateRange = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    
    weeklyData.push({
      week: dateRange,
      workouts: weekWorkouts.length,
      totalMinutes: weekWorkouts.reduce((sum, w) => sum + (w.duration_minutes || 0), 0)
    });
  }

  // Workout type distribution
  const workoutTypes = completedWorkouts.reduce((acc, workout) => {
    const type = workout.workout_type || 'Other';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const workoutTypeData = Object.entries(workoutTypes).map(([type, count]) => ({
    name: type,
    value: count
  }));

  // Workout type color mapping - [start color, end color] for gradients
  const WORKOUT_TYPE_COLORS: Record<string, [string, string]> = {
    'strength': ['#0AFFC2', '#00D4A1'],  // Mint green gradient
    'Strength': ['#0AFFC2', '#00D4A1'],
    'cardio': ['#1765FF', '#0EA5E9'],    // Blue gradient
    'Cardio': ['#1765FF', '#0EA5E9'],
    'running': ['#1765FF', '#0EA5E9'],   // Blue gradient
    'Running': ['#1765FF', '#0EA5E9'],
    'hiit': ['#FF6666', '#F43F5E'],      // Red/Coral gradient
    'HIIT': ['#FF6666', '#F43F5E'],
    'custom': ['#F5B52A', '#F59E0B'],    // Orange/Gold gradient
    'Custom': ['#F5B52A', '#F59E0B'],
    'Other': ['#F5B52A', '#F59E0B'],
  };
  
  // Get colors for a workout type (with fallback)
  const getWorkoutColors = (type: string): [string, string] => {
    return WORKOUT_TYPE_COLORS[type] || WORKOUT_TYPE_COLORS[type.toLowerCase()] || ['#F5B52A', '#F59E0B'];
  };

  return (
    <div className="bg-gradient-bg p-2 sm:p-6 pb-24">
      <div className="w-full mx-auto space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-xl sm:text-3xl font-normal text-foreground">
            YOUR FITNESS JOURNEY
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Track your progress, celebrate milestones, and optimize your performance
          </p>
        </div>

        {/* Progress Overview Section */}
        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-1">
          <GoalProgressCard
            goals={goals}
            onCreateGoal={handleCreateGoal}
            onUpdateProgress={handleUpdateProgress}
            onDeleteGoal={handleDeleteGoal}
          />
        </div>

        {/* Combined Progress Overview */}
        <CombinedProgressChart
          workouts={workouts}
          sleepLogs={sleepLogs}
          nutritionLogs={nutritionLogs}
          goals={goals}
          nutritionGoals={nutritionGoals}
          dailyNutritionData={dailyNutritionData}
        />

        {/* Enhanced Workout Analytics */}
        <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          {/* Stats Cards */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <div>
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Total Workouts</p>
                  <p className="text-lg sm:text-2xl font-bold">{completedWorkouts.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <div>
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Weekly Average</p>
                  <p className="text-lg sm:text-2xl font-bold">{workoutsPerWeek.toFixed(1)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-2">
                <Target className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <div>
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Total Time</p>
                  <p className="text-lg sm:text-2xl font-bold">{Math.round(totalWorkoutTime / 60)}h</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-2">
                <Award className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <div>
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Active Goals</p>
                  <p className="text-lg sm:text-2xl font-bold">{goals.filter(g => g.is_active).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Workout Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-normal">
                <TrendingUp className="h-5 w-5 text-primary" />
                Workout Frequency Trend
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 md:px-6">
              {weeklyData.length > 0 ? (
                <div 
                  className="overflow-x-auto pb-4 -mx-2 px-2 md:mx-0 md:px-0 scrollbar-integrated scroll-smooth"
                >
                  <div className="min-w-[500px] md:min-w-0">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={weeklyData}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="week" />
                        <YAxis />
                        <Tooltip />
                        <Line 
                          type="monotone" 
                          dataKey="workouts" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={2}
                          dot={false}
                          activeDot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No workout data available yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Workout Type Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-normal">
                <Award className="h-5 w-5 text-primary" />
                Workout Types
              </CardTitle>
            </CardHeader>
            <CardContent>
              {workoutTypeData.length > 0 ? (
                <div className="space-y-4">
                  <div className="relative">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <defs>
                          {workoutTypeData.map((entry, index) => {
                            const [startColor, endColor] = getWorkoutColors(entry.name);
                            return (
                              <linearGradient 
                                key={`gradient-${index}`} 
                                id={`workoutGradient-${index}`} 
                                x1="0%" 
                                y1="0%" 
                                x2="100%" 
                                y2="100%"
                              >
                                <stop offset="0%" stopColor={startColor} stopOpacity={1} />
                                <stop offset="100%" stopColor={endColor} stopOpacity={1} />
                              </linearGradient>
                            );
                          })}
                          {/* Ambient glow filters */}
                          {workoutTypeData.map((entry, index) => {
                            const [startColor] = getWorkoutColors(entry.name);
                            return (
                              <filter key={`glow-${index}`} id={`workoutGlow-${index}`} x="-50%" y="-50%" width="200%" height="200%">
                                <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
                                <feFlood floodColor={startColor} floodOpacity="0.5" result="glowColor"/>
                                <feComposite in="glowColor" in2="coloredBlur" operator="in" result="softGlow"/>
                                <feMerge>
                                  <feMergeNode in="softGlow"/>
                                  <feMergeNode in="softGlow"/>
                                  <feMergeNode in="SourceGraphic"/>
                                </feMerge>
                              </filter>
                            );
                          })}
                        </defs>
                        <Pie
                          data={workoutTypeData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={0}
                          dataKey="value"
                          stroke="none"
                          startAngle={90}
                          endAngle={-270}
                        >
                          {workoutTypeData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={`url(#workoutGradient-${index})`}
                              filter={`url(#workoutGlow-${index})`}
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center label with total count */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-foreground">
                          {workoutTypeData.reduce((sum, entry) => sum + entry.value, 0)}
                        </div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">
                          Total
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {workoutTypeData.map((entry) => (
                      <div key={entry.name} className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ background: `linear-gradient(135deg, ${getWorkoutColors(entry.name)[0]}, ${getWorkoutColors(entry.name)[1]})` }}
                        />
                        <span className="text-sm text-muted-foreground">
                          {entry.name}: {entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  Complete some workouts to see the breakdown
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Exercise Progress - Clickable List */}
        <ExerciseList />

        {/* Exercise Progress Charts */}
        <ExerciseProgressCharts />

        {/* Milestone Celebration Modal */}
        <MilestoneCelebration
          milestone={activeMilestone}
          goal={milestoneGoal}
          isOpen={showMilestone}
          onClose={handleCloseMilestone}
        />

        {/* Share Workout Card */}
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-normal">
              <ShareIcon className="h-5 w-5 text-primary" />
              Share Workout
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Share your completed workouts to Instagram, Snapchat, or save to your photo library - just like Strava!
            </p>
            
            {completedWorkouts.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {completedWorkouts.slice(0, 10).map((workout) => (
                  <div 
                    key={workout.id}
                    className="flex items-center justify-between p-3 bg-background/20 rounded-lg border border-border/30"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{workout.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(workout.completed_at!).toLocaleDateString()} • {workout.duration_minutes || 0} min
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleShareWorkout(workout)}
                      className="ml-2 shrink-0"
                    >
                      <ShareIcon className="h-4 w-4 text-primary" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                Complete a workout to share it!
              </p>
            )}
          </CardContent>
        </Card>

        {/* Account Settings */}
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-normal">
              <Target className="h-5 w-5 text-accent" />
              Account Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-background/10 rounded-lg">
              {profile?.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt="Profile" 
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center text-black font-bold text-xl">
                  {profile?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                  {profile?.last_name?.[0]?.toUpperCase() || ''}
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-lg">
                  {profile?.first_name && profile?.last_name 
                    ? `${profile.first_name} ${profile.last_name}`
                    : user?.email?.split('@')[0] || 'User'
                  }
                </h3>
                <p className="text-sm text-muted-foreground">
                  Member since {new Date(profile?.created_at || user?.created_at || '').toLocaleDateString('en-US', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Button
                variant="outline"
                onClick={() => navigate('/settings')}
                className="w-full justify-center"
              >
                Edit Profile
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                AI Coach Name: <span className="font-medium text-foreground">{profile?.ai_coach_name || 'Arnold'}</span> — change it in Edit Profile
              </p>
              
              <div className="flex items-center justify-between w-full h-10 px-4 py-2 rounded-md border border-input bg-background text-sm font-medium">
                <span className="w-8"></span>
                <span className="flex-1 text-center">
                  Appearance
                </span>
                <ThemeToggle size="sm" />
              </div>
              
              <Link to="/terms" className="block">
                <Button variant="outline" className="w-full justify-center">
                  Terms and Conditions
                </Button>
              </Link>
              
              <Link to="/privacy" className="block">
                <Button variant="outline" className="w-full justify-center">
                  Privacy Policy
                </Button>
              </Link>
              
              <Link to="/support" className="block">
                <Button variant="outline" className="w-full justify-center">
                  Support
                </Button>
              </Link>
              
              <Button
                onClick={handleLogout}
                variant="outline" 
                className="w-full justify-center"
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Share Workout Modal */}
        <ShareWorkoutModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          workout={selectedWorkoutForShare}
        />
      </div>
    </div>
  );
};